import { useState, useEffect, useRef } from 'react';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { publishCornerHighlight } from '../../hooks/useCornerHighlight';
import useStreamingAutoScroll from '../../hooks/useStreamingAutoScroll';
import { getCornerInsight } from '../../services/api';
import CornerInsight from './CornerInsight';
import SessionRecapContent from './SessionRecapContent';
import styles from './MetricsPanel.module.css';

const M_TO_FT = 3.28084;

// Hover payload for a corner card — used by traces (distance band) and the
// map (apex halo). A corner's entry_dist_m / exit_dist_m are spans RELATIVE to
// the apex (see backend corner_detect.py), so the trace band needs the apex's
// absolute lap distance (from the corner's per-lap metrics). Spans are
// approximate, so the band is a soft cue, not an official corner boundary.
function cornerHighlightDetail(corner, apexDistM = null) {
  if (!corner) return null;
  const detail = {
    cornerId: corner.id,
    label:    corner.label || corner.id,
    lat:      corner.apex_lat ?? corner.lat,
    lon:      corner.apex_lon ?? corner.lon,
  };
  if (apexDistM != null) {
    const eSpan = corner.entry_dist_m > 0 ? corner.entry_dist_m : 100;
    const xSpan = corner.exit_dist_m > 0 ? corner.exit_dist_m : 100;
    detail.distFtMin = Math.max(0, apexDistM - eSpan) * M_TO_FT;
    detail.distFtMax = (apexDistM + xSpan) * M_TO_FT;
  }
  return detail;
}

const METRIC_DEFS = [
  { key: 'brake_app_dist_m',        label: 'Brake dist',      unit: 'm',    higherIsBetter: false, tip: 'Metres before apex where braking begins' },
  { key: 'peak_brake_g',            label: 'Peak brake',       unit: 'G',    higherIsBetter: true,  tip: 'Maximum deceleration force' },
  { key: 'min_speed',               label: 'Min speed',        unit: 'mph',  higherIsBetter: true,  tip: 'Lowest speed in corner — higher = more speed carried' },
  { key: 'min_speed_offset_m',      label: 'Min spd offset',   unit: 'm',    higherIsBetter: null,  tip: 'Distance of min-speed point relative to apex (– before, + after)' },
  { key: 'throttle_pickup_m',       label: 'Throttle pickup',  unit: 'm',    higherIsBetter: false, tip: 'Metres past apex when throttle opens — lower = earlier' },
  { key: 'throttle_ramp_pct_per_m', label: 'Throttle ramp',    unit: '%/m',  higherIsBetter: true,  tip: 'Throttle application rate — higher = more confident' },
  { key: 'peak_lat_g',              label: 'Peak lat G',       unit: 'G',    higherIsBetter: true,  tip: 'Maximum lateral load — higher = more tyre grip used' },
  { key: 'peak_gsum',               label: 'Peak GSum',        unit: 'G',    higherIsBetter: true,  tip: '√(lat²+lon²) — traction circle loading; how close to the grip limit' },
  { key: 'exit_speed',              label: 'Exit speed',       unit: 'mph',  higherIsBetter: true,  tip: 'Speed at 75m past apex' },
  { key: 'trail_brake_overlap_m',   label: 'Trail brake',      unit: 'm',    higherIsBetter: true,  tip: 'Metres of brake overlap past lateral-G onset — higher = more trail braking' },
];

function fmt(val, decimals = 1) {
  if (val == null) return '—';
  return Number(val).toFixed(decimals);
}

// ── Speed Profile helpers ──────────────────────────────────────────────────────

function phaseAvg(phases, key) {
  const vals = phases.map(p => p[key]).filter(v => v != null);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function phaseMostCommon(phases, key) {
  const vals = phases.map(p => p[key]).filter(v => v != null);
  if (!vals.length) return null;
  const freq = {};
  vals.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
}

function SpeedProfileSection({ perLap, lapLabels }) {
  const phases = perLap.map(p => p.phase).filter(Boolean);
  if (!phases.length) return null;

  const avg = key => phaseAvg(phases, key);
  const mc  = key => phaseMostCommon(phases, key);

  const avgCoast     = avg('coast_m');
  const avgVminHold  = avg('vmin_hold_m');
  const avgHes       = avg('throttle_hesitation_count');
  const brakeRelType = mc('brake_release_type');
  const hasFindings  = avgCoast != null || brakeRelType != null
    || (avgVminHold != null && avgVminHold > 12)
    || (avgHes != null && avgHes >= 0.5);

  const spd = (val, dec = 1) => val != null ? Number(val).toFixed(dec) : '—';
  const arrow = (a, b) => (a != null && b != null) ? `${spd(a)}→${spd(b)}` : spd(a);

  return (
    <div className={styles.phaseSection}>
      <div className={styles.phaseSectionTitle}>Speed Profile</div>
      <table className={styles.phaseTable}>
        <thead>
          <tr>
            <th className={styles.phaseNameHdr}>Phase</th>
            <th className={styles.phaseAvgHdr}>Avg</th>
            {perLap.map(p => (
              <th key={p.lap_index} className={styles.phaseLapHdr}>
                {lapLabels[p.lap_index] || `L${p.lap_index}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>

          {/* Approach */}
          {avg('approach_speed') != null && (
            <tr>
              <td className={styles.phaseName}>Approach</td>
              <td className={styles.phaseAvg}>
                <span className={styles.phdSpd}>{spd(avg('approach_speed'))} mph</span>
                {avg('approach_stable_m') != null &&
                  <span className={styles.phdDetail}> stable {spd(avg('approach_stable_m'), 0)}m</span>}
              </td>
              {perLap.map(p => (
                <td key={p.lap_index} className={styles.phaseLap}>
                  {spd(p.phase?.approach_speed)}
                </td>
              ))}
            </tr>
          )}

          {/* Brake */}
          {avg('brake_entry_speed') != null && (
            <tr>
              <td className={styles.phaseName}>Brake</td>
              <td className={styles.phaseAvg}>
                <span className={styles.phdSpd}>{arrow(avg('brake_entry_speed'), avg('brake_exit_speed'))} mph</span>
                {avg('brake_dist_m') != null &&
                  <span className={styles.phdDetail}> {spd(avg('brake_dist_m'), 0)}m</span>}
                {avg('peak_decel_g') != null &&
                  <span className={styles.phdDetail}> {spd(avg('peak_decel_g'), 2)}G
                    {avg('peak_decel_pct') != null &&
                      <span> @{(avg('peak_decel_pct') * 100).toFixed(0)}%</span>}
                  </span>}
              </td>
              {perLap.map(p => (
                <td key={p.lap_index} className={styles.phaseLap}>
                  {p.phase?.brake_entry_speed != null
                    ? arrow(p.phase.brake_entry_speed, p.phase.brake_exit_speed)
                    : '—'}
                </td>
              ))}
            </tr>
          )}

          {/* Trail brake */}
          {avg('trail_entry_speed') != null && (avg('trail_dist_m') ?? 0) > 2 && (
            <tr>
              <td className={styles.phaseName}>Trail brake</td>
              <td className={styles.phaseAvg}>
                <span className={styles.phdSpd}>{arrow(avg('trail_entry_speed'), avg('trail_exit_speed'))} mph</span>
                {avg('trail_dist_m') != null &&
                  <span className={styles.phdDetail}> {spd(avg('trail_dist_m'), 0)}m</span>}
                {avg('trail_decel_start_g') != null && avg('trail_decel_end_g') != null &&
                  <span className={styles.phdDetail}>
                    {' '}{spd(avg('trail_decel_start_g'), 2)}→{spd(avg('trail_decel_end_g'), 2)}G
                  </span>}
              </td>
              {perLap.map(p => (
                <td key={p.lap_index} className={styles.phaseLap}>
                  {p.phase?.trail_entry_speed != null
                    ? arrow(p.phase.trail_entry_speed, p.phase.trail_exit_speed)
                    : '—'}
                </td>
              ))}
            </tr>
          )}

          {/* vMin */}
          {avg('vmin_speed') != null && (
            <tr>
              <td className={styles.phaseName}>vMin</td>
              <td className={styles.phaseAvg}>
                <span className={`${styles.phdSpd} ${styles.phdVmin}`}>{spd(avg('vmin_speed'))} mph</span>
                {(avg('vmin_hold_m') ?? 0) > 3 &&
                  <span className={styles.phdDetail}> held {spd(avg('vmin_hold_m'), 0)}m</span>}
                {avg('vmin_offset_m') != null && (() => {
                  const o = avg('vmin_offset_m');
                  return <span className={styles.phdDetail}> {o >= 0 ? `+${spd(o, 0)}` : spd(o, 0)}m apex</span>;
                })()}
              </td>
              {perLap.map(p => (
                <td key={p.lap_index} className={`${styles.phaseLap} ${styles.phaseLapVmin}`}>
                  {spd(p.phase?.vmin_speed)}
                </td>
              ))}
            </tr>
          )}

          {/* Exit */}
          {avg('exit_start_speed') != null && (
            <tr>
              <td className={styles.phaseName}>Exit</td>
              <td className={styles.phaseAvg}>
                <span className={styles.phdSpd}>{arrow(avg('exit_start_speed'), avg('exit_end_speed'))} mph</span>
                {avg('exit_dist_m') != null &&
                  <span className={styles.phdDetail}> {spd(avg('exit_dist_m'), 0)}m</span>}
                {(avg('exit_accel_start_g') ?? 0) > 0 && (avg('exit_accel_end_g') ?? 0) > 0 &&
                  <span className={styles.phdDetail}>
                    {' '}+{spd(avg('exit_accel_start_g'), 2)}→+{spd(avg('exit_accel_end_g'), 2)}G
                  </span>}
              </td>
              {perLap.map(p => (
                <td key={p.lap_index} className={styles.phaseLap}>
                  {p.phase?.exit_start_speed != null
                    ? arrow(p.phase.exit_start_speed, p.phase.exit_end_speed)
                    : '—'}
                </td>
              ))}
            </tr>
          )}

        </tbody>
      </table>

      {hasFindings && (
        <>
          <div className={`${styles.phaseSectionTitle} ${styles.findingsTitle}`}>Findings</div>
          <table className={styles.phaseTable}>
            <tbody>

              {avgCoast != null && (
                <tr>
                  <td className={`${styles.phaseName} ${styles.findingLabel}`}>Coast</td>
                  <td className={styles.phaseAvg}>
                    <span className={styles.phdFinding}>{spd(avgCoast, 0)}m</span>
                    {avg('coast_s') != null &&
                      <span className={styles.phdDetail}> / {spd(avg('coast_s'), 2)}s avg</span>}
                  </td>
                  {perLap.map(p => (
                    <td key={p.lap_index} className={`${styles.phaseLap} ${styles.phaseLapFinding}`}>
                      {p.phase?.coast_m != null ? `${spd(p.phase.coast_m, 0)}m` : '—'}
                    </td>
                  ))}
                </tr>
              )}

              {avgVminHold != null && avgVminHold > 12 && (
                <tr>
                  <td className={`${styles.phaseName} ${styles.findingLabel}`}>vMin hold</td>
                  <td className={styles.phaseAvg}>
                    <span className={styles.phdFinding}>{spd(avgVminHold, 0)}m avg</span>
                  </td>
                  {perLap.map(p => (
                    <td key={p.lap_index} className={`${styles.phaseLap} ${styles.phaseLapFinding}`}>
                      {p.phase?.vmin_hold_m != null ? `${spd(p.phase.vmin_hold_m, 0)}m` : '—'}
                    </td>
                  ))}
                </tr>
              )}

              {brakeRelType != null && (
                <tr>
                  <td className={`${styles.phaseName} ${styles.findingLabel}`}>Brake rel.</td>
                  <td className={styles.phaseAvg}>
                    <span className={styles.phdFinding}>{brakeRelType}</span>
                    {avg('brake_release_dist_m') != null &&
                      <span className={styles.phdDetail}> {spd(avg('brake_release_dist_m'), 0)}m</span>}
                  </td>
                  {perLap.map(p => (
                    <td key={p.lap_index} className={`${styles.phaseLap} ${styles.phaseLapFinding}`}>
                      {p.phase?.brake_release_type ? p.phase.brake_release_type.slice(0, 3) : '—'}
                    </td>
                  ))}
                </tr>
              )}

              {avgHes != null && avgHes >= 0.5 && (
                <tr>
                  <td className={`${styles.phaseName} ${styles.findingLabel}`}>Hesitations</td>
                  <td className={styles.phaseAvg}>
                    <span className={styles.phdFinding}>{avgHes.toFixed(1)} avg</span>
                  </td>
                  {perLap.map(p => (
                    <td key={p.lap_index} className={`${styles.phaseLap} ${styles.phaseLapFinding}`}>
                      {p.phase?.throttle_hesitation_count ?? '—'}
                    </td>
                  ))}
                </tr>
              )}

            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function decimalsFor(key) {
  return (key.includes('_g') || key === 'peak_gsum' || key === 'throttle_ramp_pct_per_m') ? 3 : 1;
}

function stdBadge(std, mean) {
  if (std == null || mean == null || mean === 0) return null;
  const pct = (std / Math.abs(mean)) * 100;
  if (pct > 20) return { cls: styles.badgeHigh, label: `±${std.toFixed(1)}` };
  if (pct > 8)  return { cls: styles.badgeMid,  label: `±${std.toFixed(1)}` };
  return null;
}

const TB_LAP_INDEX = -1;

function CornerCard({ cornerId, label, corner, data, lapTraces, selectedLaps, lapNumberByIndex,
                      insight, sessionId, loadedSessionIds, onLoadSession, canAddMore,
                      focusRequest, onFocusHandled }) {
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);
  const cardRef = useRef(null);
  const { per_lap = [], agg = {} } = data;

  useEffect(() => {
    if (!focusRequest) return undefined;
    setExpanded(true);
    setFocused(true);
    publishCornerHighlight(cornerHighlightDetail(
      corner, per_lap.find(p => p.apex_dist_m != null)?.apex_dist_m ?? null));
    const frame = requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => {
      setFocused(false);
      publishCornerHighlight(null);
      onFocusHandled?.();
    }, 1800);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      publishCornerHighlight(null);
    };
  }, [focusRequest?.requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const lapLabels = {};
  selectedLaps.forEach(li => {
    const trace = lapTraces?.[li];
    lapLabels[li] = trace?.lap_time_str || `L${li + 1}`;
  });
  // Always label TB if present (it might not be in selectedLaps but is in per_lap)
  lapLabels[TB_LAP_INDEX] = 'TB';

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${focused ? styles.cardFocused : ''}`}
      onMouseEnter={() => publishCornerHighlight(cornerHighlightDetail(
        corner, per_lap.find(p => p.apex_dist_m != null)?.apex_dist_m ?? null))}
      onMouseLeave={() => publishCornerHighlight(null)}
    >
      <button className={styles.cardHeader} onClick={() => setExpanded(v => !v)}>
        <span className={styles.cornerBadge}>{label || cornerId}</span>
        <span className={styles.cardToggle}>{expanded ? '▲' : '▼'}</span>
        {agg.min_speed && (
          <span className={styles.quickStat}>
            {fmt(agg.min_speed.min)}–{fmt(agg.min_speed.max)} mph
          </span>
        )}
        {agg.brake_app_dist_m && (
          <span className={styles.quickStat}>
            Brake {fmt(agg.brake_app_dist_m.mean)}m
            {agg.brake_app_dist_m.std > 5 && (
              <span className={styles.badgeHigh}> ±{fmt(agg.brake_app_dist_m.std)}</span>
            )}
          </span>
        )}
        {agg.peak_gsum && (
          <span className={styles.quickStat} title="Peak traction circle load">
            GSum {fmt(agg.peak_gsum.max, 2)}G
          </span>
        )}
      </button>

      {expanded && (
        <div className={styles.cardBody}>
          <div className={styles.cardBodyInner}>
          <table className={styles.metricTable}>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Mean</th>
                <th>Spread</th>
                <th>Best</th>
                <th>Worst</th>
                {per_lap.map(p => (
                  <th
                    key={p.lap_index}
                    className={styles.lapCol}
                    style={p.lap_index === TB_LAP_INDEX ? { color: '#FFD700', fontStyle: 'italic' } : {}}
                  >
                    {lapLabels[p.lap_index] || `L${p.lap_index}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRIC_DEFS.map(({ key, label: mLabel, unit, tip, higherIsBetter }) => {
                const a = agg[key];
                const badge = a ? stdBadge(a.std, a.mean) : null;
                const dec = decimalsFor(key);

                let bestVal = '—', worstVal = '—';
                if (a) {
                  if (higherIsBetter === true)  { bestVal = fmt(a.max, dec); worstVal = fmt(a.min, dec); }
                  else if (higherIsBetter === false) { bestVal = fmt(a.min, dec); worstVal = fmt(a.max, dec); }
                  else { bestVal = fmt(a.min, dec); worstVal = fmt(a.max, dec); }
                }

                return (
                  <tr key={key} title={tip}>
                    <td className={styles.metricName}>
                      {mLabel}<span className={styles.unit}> {unit}</span>
                      {higherIsBetter === true  && <span className={styles.dirHint} title="Higher is better"> ↑</span>}
                      {higherIsBetter === false && <span className={styles.dirHint} title="Lower is better"> ↓</span>}
                    </td>
                    <td className={styles.num}>{a ? fmt(a.mean, dec) : '—'}</td>
                    <td className={styles.num}>
                      {badge
                        ? <span className={badge.cls}>{badge.label}</span>
                        : (a ? <span className={styles.badgeOk}>✓</span> : '—')}
                    </td>
                    <td className={`${styles.num} ${styles.best}`}>{bestVal}</td>
                    <td className={`${styles.num} ${styles.worst}`}>{worstVal}</td>
                    {per_lap.map(p => {
                      const isTb = p.lap_index === TB_LAP_INDEX;
                      const srcLapNum = isTb && p.source_lap_index != null
                        ? (lapNumberByIndex?.[p.source_lap_index] ?? p.source_lap_index + 1)
                        : null;
                      return (
                        <td
                          key={p.lap_index}
                          className={styles.num}
                          style={isTb ? { color: '#FFD700', fontStyle: 'italic' } : {}}
                          title={isTb && srcLapNum != null ? `From lap ${srcLapNum}` : undefined}
                        >
                          {fmt(p[key], dec)}
                          {isTb && srcLapNum != null && (
                            <sup style={{ fontSize: '0.65em', opacity: 0.7, marginLeft: 2 }}>
                              L{srcLapNum}
                            </sup>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <SpeedProfileSection perLap={per_lap} lapLabels={lapLabels} />
          {insight && (() => {
            // Map hover geometry: apex lap distance + a loaded trace for
            // lat/lon lookup (prefer the lap the apex distance came from).
            const apexLap = per_lap.find(p => p.apex_dist_m != null);
            const trace = lapTraces?.[apexLap?.lap_index]
              || (selectedLaps || []).map(li => lapTraces?.[li]).find(t => t?.points?.length);
            return (
              <CornerInsight cornerId={cornerId} insight={insight}
                             corner={corner} apexDistM={apexLap?.apex_dist_m}
                             tracePoints={trace?.points}
                             currentSessionId={sessionId}
                             loadedSessionIds={loadedSessionIds}
                             onLoadSession={onLoadSession} canAddMore={canAddMore} />
            );
          })()}
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentsTable({ segments, lapTraces }) {
  if (!segments || segments.length === 0) return null;

  const byPair = {};
  for (const s of segments) {
    const key = `${s.from_corner_id}→${s.to_corner_id}`;
    if (!byPair[key]) byPair[key] = { from: s.from_corner_id, to: s.to_corner_id, laps: [] };
    byPair[key].laps.push(s);
  }

  const lapLabel = (lapIndex) => lapTraces?.[lapIndex]?.lap_time_str || `L${lapIndex + 1}`;

  return (
    <div className={styles.segSection}>
      <div className={styles.segTitle}>Segments</div>
      <table className={styles.metricTable}>
        <thead>
          <tr>
            <th>Segment</th>
            <th>Lap</th>
            <th>Dist</th>
            <th>Max spd</th>
            <th>Avg spd</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byPair).map(([key, { from, to, laps }]) =>
            laps.map((lap, i) => (
              <tr key={`${key}-${lap.lap_index}`}>
                <td className={styles.metricName} style={{ opacity: i === 0 ? 1 : 0.3 }}>
                  {i === 0 ? `${from} → ${to}` : ''}
                </td>
                <td className={styles.lapPill}>{lapLabel(lap.lap_index)}</td>
                <td className={styles.num}>{lap.dist_m != null ? `${lap.dist_m.toFixed(0)}m` : '—'}</td>
                <td className={`${styles.num} ${styles.best}`}>{lap.max_speed != null ? `${lap.max_speed.toFixed(1)}` : '—'}</td>
                <td className={styles.num}>{lap.avg_speed != null ? `${lap.avg_speed.toFixed(1)}` : '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function EnvelopeBar({ envelope, accentColor }) {
  if (!envelope) return null;
  const parts = [];
  if (envelope.max_brake_g) parts.push(`${envelope.max_brake_g.toFixed(2)}G brake`);
  if (envelope.max_lat_g)   parts.push(`${envelope.max_lat_g.toFixed(2)}G lat`);
  if (envelope.max_gsum)    parts.push(`${envelope.max_gsum.toFixed(2)}G sum`);
  if (envelope.max_speed)   parts.push(`${envelope.max_speed.toFixed(0)} mph`);
  if (!parts.length) return null;
  return (
    <span className={styles.envelope} style={{ color: accentColor }}>
      {parts.join(' · ')}
    </span>
  );
}

// ── Per-sector metrics (parallels Split Times; uses the SPEED TRACE sectors) ────

const SECTOR_METRIC_DEFS = [
  { key: 'time_s',       label: 'Time',    unit: 's', dec: 3, higherBetter: false, avail: () => true },
  { key: 'min_speed',    label: 'Min Spd', unit: '',  dec: 0, higherBetter: true,  avail: a => a.speed },
  { key: 'avg_speed',    label: 'Avg Spd', unit: '',  dec: 0, higherBetter: true,  avail: a => a.speed },
  { key: 'max_speed',    label: 'Max Spd', unit: '',  dec: 0, higherBetter: true,  avail: a => a.speed },
  { key: 'peak_brake_g', label: 'Brake G', unit: 'G', dec: 2, higherBetter: true,  avail: a => a.brake_g },
  { key: 'peak_lat_g',   label: 'Lat G',   unit: 'G', dec: 2, higherBetter: true,  avail: a => a.lat_g },
];

function SectorMetricsTable({ data, sectors, selectedLaps, lapNumberByIndex }) {
  const [metricKey, setMetricKey] = useState('min_speed');
  if (!data?.laps?.length || !sectors?.length) return null;

  const avail   = data.available || {};
  const defs    = SECTOR_METRIC_DEFS.filter(m => m.avail(avail));
  if (!defs.length) return null;
  const metric  = defs.find(m => m.key === metricKey) || defs[0];

  const rows = data.laps.filter(l =>
    !l.is_outlap && !l.is_inlap
    && (!selectedLaps || selectedLaps.includes(l.lap_index))
  );
  if (!rows.length) return null;

  // Best value per sector column (direction depends on the metric).
  const best = sectors.map((_, i) => {
    const vals = rows.map(l => l.sectors?.[i]?.[metric.key]).filter(v => v != null);
    if (!vals.length) return null;
    return metric.higherBetter ? Math.max(...vals) : Math.min(...vals);
  });

  return (
    <div className={styles.sectorMetrics}>
      <div className={styles.sectorMetricsHead}>
        <span className={styles.sectorMetricsTitle}>Sector Metrics</span>
        <div className={styles.metricTabs}>
          {defs.map(m => (
            <button
              key={m.key}
              className={`${styles.metricTab} ${m.key === metric.key ? styles.metricTabActive : ''}`}
              onClick={() => setMetricKey(m.key)}
            >{m.label}</button>
          ))}
        </div>
      </div>
      <div className={styles.sectorTableWrap}>
        <table className={styles.sectorTable}>
          <thead>
            <tr>
              <th>Lap</th>
              {sectors.map(s => <th key={s.id}>{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(l => (
              <tr key={l.lap_index}>
                <td className={styles.sectorLapCell}>
                  {lapNumberByIndex?.[l.lap_index] ?? l.lap_number}{l.is_inlap ? ' (in)' : ''}
                </td>
                {sectors.map((s, i) => {
                  const v = l.sectors?.[i]?.[metric.key];
                  const isBest = v != null && best[i] != null && Math.abs(v - best[i]) < 1e-6;
                  return (
                    <td key={s.id} className={isBest ? styles.bestCell : ''}>
                      {v == null ? '—' : `${v.toFixed(metric.dec)}${metric.unit ? ` ${metric.unit}` : ''}`}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.sectorUnitHint}>
        Speeds in {data.speed_unit || 'mph'} · green = best per sector
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MetricsPanel({
  corners, lapTraces,
  metrics,  loading,  error,  selectedLaps,
  driverName1,
  // Primary session id — enables the per-corner insight section (Driver 1 only)
  sessionId,
  // Server feature flag. Unlike the admin prompt debugger, this regular-user
  // flow is available to every authorized viewer, including public links.
  enableAi = true,
  // Admin-only affordance for inspecting the exact recap payload sent to the
  // model. The API response already has to contain these prompts so the browser
  // can call Ollama; this only controls their in-panel debug presentation.
  allowPromptDebug = false,
  // Shared session-scoped recap state. The AI Debrief dialog and this panel
  // render the same stream and never issue duplicate generation requests.
  recapController = null,
  // One-shot request from Deep Analysis chat: expand, scroll to, and briefly
  // highlight the referenced primary-driver corner.
  focusCornerRequest = null,
  onFocusCornerHandled = null,
  // "Load that session" popup on insight-chart click: loads the best-pass
  // session into a free comparison slot (null = viewer can't load sessions)
  onLoadSession, loadedSessionIds = [], canAddMore = true,
  // Per-sector metrics (one table per driver), keyed off the SPEED TRACE sectors
  sectors, sectorMetrics,
  // Mapping from lap_index → display lap number, used for TB source-lap superscripts
  lapNumberByIndex,
  // Comparison drivers — each provides its own metrics/laps/etc. (see App.jsx).
  compareDrivers = [],
}) {
  // One descriptor per driver tab: the primary first, then each comparison driver.
  const drivers = [
    {
      name: driverName1 || 'Driver 1', accent: '#e8411a',
      metrics, loading, error,
      selectedLaps, lapTraces, sectorMetrics, lapNumberByIndex,
    },
    ...compareDrivers.map(d => ({
      name: d.name, accent: d.accent,
      metrics: d.metrics, loading: d.metricsLoading, error: d.metricsError,
      selectedLaps: d.selectedLaps, lapTraces: d.lapTraces,
      sectorMetrics: d.sectorMetrics, lapNumberByIndex: d.lapNumberByIndex,
    })),
  ];
  const hasMany = drivers.length > 1;
  const [activeDriver, setActiveDriver] = useState(0);
  const idx = Math.min(activeDriver, drivers.length - 1);
  const d = drivers[idx];
  useEffect(() => {
    if (focusCornerRequest) setActiveDriver(0);
  }, [focusCornerRequest]);

  // ── Corner insight cache (Driver 1's session only) ──────────────────────────
  // One fetch per session — self mode only for now (the cross-driver "field"
  // mode stays available server-side for a future iteration). null = not
  // loaded yet (CornerInsight shows a loading row).
  const [insight, setInsight] = useState({ self: null });
  const insightReqRef = useRef(false);
  useEffect(() => {
    setInsight({ self: null });
    insightReqRef.current = false;
  }, [sessionId]);
  const hasMetrics = !!metrics;
  useEffect(() => {
    if (!hasMetrics || !sessionId || insightReqRef.current) return;
    insightReqRef.current = true;
    getCornerInsight(sessionId, 'self')
      .then(d2 => setInsight({ self: d2 }))
      .catch(() => setInsight({ self: { corners: {} } }));
  }, [hasMetrics, sessionId]);

  const active     = d.metrics;
  const activeLT   = d.lapTraces;
  const activeSL   = d.selectedLaps;
  const activeLapNum = d.lapNumberByIndex;
  const activeName = d.name;
  const activeLoad = d.loading;
  const activeErr  = d.error;
  const activeSL_  = d.selectedLaps;
  const activeSectorMetrics = d.sectorMetrics;

  // ── History-aware inline session recap (primary driver only) ──────────────
  const [showRecapPrompts, setShowRecapPrompts] = useState(false);
  const recap = recapController?.recap || null;
  const recapBusy = !!recapController?.busy;
  const recapAnyBusy = !!recapController?.anyBusy;
  const recapModels = recapController?.models || [];
  const recapModelsLoading = !!recapController?.modelsLoading;
  const recapModelsError = recapController?.modelsError || null;
  const recapModel = recapController?.model || '';
  const runRecap = recapController?.generate;
  const stopRecap = recapController?.stop;
  const dismissRecap = recapController?.dismiss;
  const handleRecapModelChange = event => recapController?.setModel(event.target.value);
  const {
    containerRef: recapBodyRef,
    onScroll: handleRecapBodyScroll,
    onWheel: handleRecapBodyWheel,
    onTouchStart: handleRecapBodyTouchStart,
    onTouchMove: handleRecapBodyTouchMove,
    onKeyDown: handleRecapBodyKeyDown,
  } = useStreamingAutoScroll({
    status: recap?.status,
    content: recap?.text,
  });

  const envelope  = active?.session_envelope;
  const byCorner  = active?.by_corner || {};
  const allSegs   = active?.per_lap?.flatMap(l => l.segments || []) || [];

  return (
    <div className={styles.panel}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Corner Metrics</span>}>
        <EnvelopeBar
          envelope={envelope}
          accentColor={idx > 0 ? `${d.accent}88` : undefined}
        />

        {activeLoad && <span className={styles.calcing}>Calculating…</span>}
        <HelpButton panel="metricspanel" page="sessions" />
      </PanelHeader>

      {/* Driver tab strip — only shown when a comparison session is loaded */}
      {hasMany && (
        <div className={styles.header}>
          <div className={styles.driverTabs}>
            {drivers.map((drv, i) => (
              <button
                key={i}
                className={`${styles.driverTab} ${idx === i ? styles.driverTabActive : ''}`}
                style={idx === i ? { borderBottomColor: drv.accent, color: drv.accent } : {}}
                onClick={() => setActiveDriver(i)}
              >
                {drv.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeErr && <div className={styles.error}>Error: {activeErr}</div>}

      {idx === 0 && enableAi && sessionId && recapController && (
        <div className={styles.recapWrap}>
          {!recap && (
            <div className={styles.recapLaunchRow}>
              <button className={styles.recapGenerate} onClick={runRecap}>
                <span aria-hidden="true">✦</span> Generate AI Session Recap
              </button>
              {allowPromptDebug && (
                <label className={styles.recapModelPicker}>
                  <span>Model</span>
                  <select value={recapModel} onChange={handleRecapModelChange}
                    disabled={recapAnyBusy || recapModelsLoading || !recapModels.length}
                    title={recapModelsError || 'Select the Ollama model for this recap'}>
                    {!recapModels.length && (
                      <option value="">
                        {recapModelsLoading ? 'Loading models…' : 'No models found'}
                      </option>
                    )}
                    {recapModels.map(model => (
                      <option key={model.name} value={model.name}>
                        {model.name}{model.size_gb ? ` (${model.size_gb} GB)` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {allowPromptDebug && (
                <label className={styles.recapDebugToggle}
                  title="Show the exact system and user prompts sent to Ollama">
                  <input type="checkbox" checked={showRecapPrompts}
                    onChange={e => setShowRecapPrompts(e.target.checked)} />
                  Show LLM prompts
                </label>
              )}
            </div>
          )}
          {recap && (
            <div className={styles.recapCard}>
              <div className={styles.recapHeader}>
                <div>
                  <span className={styles.recapTitle}>AI Session Recap</span>
                  {recap.meta && (
                    <span className={styles.recapMeta}>
                      {recap.meta.hasHistory
                        ? `${recap.meta.nDays} day${recap.meta.nDays === 1 ? '' : 's'} · ${recap.meta.nSessions} sessions here · any car`
                        : 'first logged day at this track'}
                    </span>
                  )}
                  {recap.status === 'streaming' && <span className={styles.recapStreaming}>streaming…</span>}
                </div>
                <div className={styles.recapActions}>
                  {allowPromptDebug && (
                    <label className={styles.recapModelPicker}>
                      <span>Model</span>
                      <select value={recapModel} onChange={handleRecapModelChange}
                        disabled={recapAnyBusy || recapModelsLoading || !recapModels.length}
                        title={recapModelsError || 'Select a model, then regenerate'}>
                        {!recapModels.length && <option value="">No models found</option>}
                        {recapModels.map(model => (
                          <option key={model.name} value={model.name}>{model.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {allowPromptDebug && (
                    <label className={styles.recapDebugToggle}
                      title="Show the exact system and user prompts sent to Ollama">
                      <input type="checkbox" checked={showRecapPrompts}
                        onChange={e => setShowRecapPrompts(e.target.checked)} />
                      Show LLM prompts
                    </label>
                  )}
                  {recapBusy ? (
                    <button onClick={stopRecap}>Stop</button>
                  ) : (
                    <button onClick={runRecap}>Regenerate</button>
                  )}
                  <button onClick={dismissRecap} title="Dismiss session recap">✕</button>
                </div>
              </div>
              <div
                className={styles.recapBody}
                ref={recapBodyRef}
                tabIndex={0}
                onScroll={handleRecapBodyScroll}
                onWheel={handleRecapBodyWheel}
                onTouchStart={handleRecapBodyTouchStart}
                onTouchMove={handleRecapBodyTouchMove}
                onKeyDown={handleRecapBodyKeyDown}
              >
                {allowPromptDebug && showRecapPrompts && recap.promptDebug && (
                  <div className={styles.recapPromptDebug}>
                    <div className={styles.recapPromptDebugHead}>
                      <span>LLM Prompt Debug</span>
                      <span>Model: {recap.promptDebug.model}</span>
                    </div>
                    {recap.promptDebug.headline && (
                      <div className={styles.recapPromptBlock}>
                        <div className={styles.recapPromptLabel}>
                          Python headline <span>(displayed directly; not sent to the LLM)</span>
                        </div>
                        <pre>{recap.promptDebug.headline}</pre>
                      </div>
                    )}
                    <div className={styles.recapPromptBlock}>
                      <div className={styles.recapPromptLabel}>System prompt</div>
                      <pre>{recap.promptDebug.system}</pre>
                    </div>
                    <div className={styles.recapPromptBlock}>
                      <div className={styles.recapPromptLabel}>User prompt</div>
                      <pre>{recap.promptDebug.user}</pre>
                    </div>
                    {recap.promptDebug.whereTime && (
                      <div className={styles.recapPromptBlock}>
                        <div className={styles.recapPromptLabel}>
                          Python “Largest Gaps to Your Proven Pace” <span>(displayed directly; not sent to the LLM)</span>
                        </div>
                        <pre>{recap.promptDebug.whereTime}</pre>
                      </div>
                    )}
                    {recap.promptDebug.nextSessionFocus && (
                      <div className={styles.recapPromptBlock}>
                        <div className={styles.recapPromptLabel}>
                          Python “Next Session Focus” <span>(displayed directly; not sent to the LLM)</span>
                        </div>
                        <pre>{recap.promptDebug.nextSessionFocus}</pre>
                      </div>
                    )}
                  </div>
                )}
                <SessionRecapContent recap={recap} variant="panel" />
              </div>
            </div>
          )}
        </div>
      )}

      {!active && !activeLoad && (
        <div className={styles.empty}>
          {hasMany
            ? `Select laps for ${activeName} — metrics calculate automatically.`
            : 'Select laps and corners — metrics calculate automatically.'}
        </div>
      )}

      {active && (
        <div className={styles.body}>
          <SectorMetricsTable
            data={activeSectorMetrics}
            sectors={sectors}
            selectedLaps={activeSL_}
            lapNumberByIndex={activeLapNum}
          />
          {corners.map(c => {
            const data = byCorner[c.id];
            if (!data) return null;
            return (
              <CornerCard
                key={c.id}
                cornerId={c.id}
                label={c.label}
                corner={c}
                data={data}
                lapTraces={activeLT}
                selectedLaps={activeSL}
                lapNumberByIndex={activeLapNum}
                insight={idx === 0 && sessionId ? insight : null}
                sessionId={sessionId} loadedSessionIds={loadedSessionIds}
                onLoadSession={onLoadSession} canAddMore={canAddMore}
                focusRequest={
                  String(focusCornerRequest?.cornerId) === String(c.id)
                    ? focusCornerRequest : null
                }
                onFocusHandled={onFocusCornerHandled}
              />
            );
          })}
          <SegmentsTable segments={allSegs} lapTraces={activeLT} />
        </div>
      )}
    </div>
  );
}
